// Partidas ASÍNCRONAS (por turnos) con Cloud Firestore (versión "lite": lecturas/escrituras
// puntuales, sin listeners en tiempo real). Cada partida es un documento en `asyncMatches`.
// Modelo:
//   { code, playerUids:[uid...], players:[{uid,name,avatar}], state:<serialize()|null>,
//     turnUid, status:'waiting'|'active'|'finished', winnerUid, createdAt, updatedAt }
import {
  getFirestore, collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, setDoc, arrayUnion,
  query, where, limit as fbLimit,
} from 'firebase/firestore/lite';

// Una sala que espera rival CADUCA a los 3 minutos: si nadie entra, desaparece.
const WAITING_TTL_MS = 3 * 60 * 1000;

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sin 0/O/1/I para leerlo bien

function makeCode(n = 5) {
  let s = '';
  for (let i = 0; i < n; i++) s += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  return s;
}

export class AsyncService {
  constructor(app) {
    this.db = getFirestore(app);
    this.col = collection(this.db, 'asyncMatches');
  }

  /** Crea una partida VACÍA (esperando rival). `me` = {uid,name,avatar}. Devuelve {id, code}. */
  async createMatch(me) {
    const code = makeCode();
    const now = Date.now();
    const data = {
      code,
      playerUids: [me.uid],
      players: [{ uid: me.uid, name: me.name || 'Jugador', avatar: me.avatar || '🙂' }],
      state: null,
      turnUid: null,
      status: 'waiting',
      winnerUid: null,
      createdAt: now,
      updatedAt: now,
    };
    const ref = await addDoc(this.col, data);
    return { id: ref.id, code };
  }

  /** Busca una partida ABIERTA por código (que no sea tuya y esté esperando). */
  async findOpenByCode(code, myUid) {
    const q = query(this.col, where('code', '==', String(code || '').toUpperCase()), fbLimit(4));
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      const m = { id: d.id, ...d.data() };
      if (m.status === 'waiting' && !m.playerUids.includes(myUid) && !this.isExpiredWaiting(m)) return m;
    }
    return null; // no hay sala abierta con ese código (o caducó)
  }

  async getMatch(id) {
    const s = await getDoc(doc(this.db, 'asyncMatches', id));
    return s.exists() ? { id: s.id, ...s.data() } : null;
  }

  async updateMatch(id, patch) {
    await updateDoc(doc(this.db, 'asyncMatches', id), { ...patch, updatedAt: Date.now() });
  }

  async deleteMatch(id) {
    try { await deleteDoc(doc(this.db, 'asyncMatches', id)); } catch (e) { console.warn('[Async] delete', e); }
  }

  /** Guarda el token FCM del dispositivo en `fcmTokens/{uid}` (para el push "es tu turno"). */
  async saveFcmToken(uid, token) {
    if (!uid || !token) return;
    try {
      await setDoc(doc(this.db, 'fcmTokens', uid), { tokens: arrayUnion(token), updatedAt: Date.now() }, { merge: true });
    } catch (e) { console.warn('[FCM] saveToken', e); }
  }

  /** ¿Sala esperando rival que ya CADUCÓ (nadie entró en 3 min)? */
  isExpiredWaiting(m) {
    return m && m.status === 'waiting' && (Date.now() - (m.createdAt || 0)) > WAITING_TTL_MS;
  }

  /** Tus partidas (más recientes primero). Se ordena en cliente para NO exigir índice compuesto.
   *  Las salas SIN rival caducadas (3 min) se borran y no se muestran. Devuelve TODAS las
   *  partidas activas/terminadas (puedes tener varias a la vez). */
  async listMine(uid, n = 30) {
    const q = query(this.col, where('playerUids', 'array-contains', uid), fbLimit(n));
    const snap = await getDocs(q);
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const alive = [];
    for (const m of rows) {
      if (this.isExpiredWaiting(m)) { this.deleteMatch(m.id); continue; } // sala caducada → fuera
      alive.push(m);
    }
    return alive;
  }
}
